import React, { useState, useEffect } from 'react';
import { leadsService } from '../services/api';
import { Lead } from '../types';

interface LeadDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  filter: {
    type: 'comprou' | 'status' | 'user';
    value: string | boolean;
    title: string;
  };
}

const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({ isOpen, onClose, filter }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  useEffect(() => {
    if (isOpen) {
      fetchFilteredLeads();
    }
  }, [isOpen, filter, currentPage]);

  const fetchFilteredLeads = async () => {
    try {
      setLoading(true);
      
      let filters: any = {
        page: currentPage,
        limit: 10
      };

      if (filter.type === 'comprou') {
        filters.comprou = filter.value;
      } else if (filter.type === 'status') {
        filters.status = filter.value;
      } else if (filter.type === 'user') {
        filters.consultor = filter.value;
      }

      const response = await leadsService.getLeads(filters);
      setLeads(response.data || response);
      
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Erro ao carregar leads filtrados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (leadId: number, newStatus: string) => {
    try {
      await leadsService.updateLeadStatus(leadId, newStatus);
      await fetchFilteredLeads();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatPhone = (phone: string | null) => {
    if (!phone) return '-';
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'novo': 'bg-blue-100 text-blue-800',
      'interessado': 'bg-yellow-100 text-yellow-800',
      'contato_inicial': 'bg-purple-100 text-purple-800',
      'em_negociacao': 'bg-orange-100 text-orange-800',
      'proposta_enviada': 'bg-indigo-100 text-indigo-800',
      'reagendar': 'bg-gray-100 text-gray-800',
      'fechado_ganho': 'bg-green-100 text-green-800',
      'fechado_perdido': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">{filter.title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Processo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Réu
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Autor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Telefone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Consultor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Comprou
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {lead.numero_processo}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{lead.reu_nome}</div>
                          <div className="text-gray-500 text-xs">{lead.reu_cpf}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{lead.autor_nome}</div>
                          <div className="text-gray-500 text-xs">{lead.autor_cpf}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatPhone(lead.reu_telefone_1)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <select
                          value={lead.status_lead || 'novo'}
                          onChange={(e) => handleStatusUpdate(lead.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full border-0 ${getStatusColor(lead.status_lead || 'novo')}`}
                        >
                          <option value="novo">Novo</option>
                          <option value="interessado">Interessado</option>
                          <option value="contato_inicial">Contato Inicial</option>
                          <option value="em_negociacao">Em Negociação</option>
                          <option value="proposta_enviada">Proposta Enviada</option>
                          <option value="reagendar">Reagendar</option>
                          <option value="fechado_ganho">Fechado Ganho</option>
                          <option value="fechado_perdido">Fechado Perdido</option>
                        </select>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lead.consultor || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          lead.comprou ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {lead.comprou ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                          title="Editar Lead"
                        >
                          ✏️
                        </button>
                        <button
                          className="text-green-600 hover:text-green-900"
                          title="WhatsApp"
                        >
                          📱
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="flex items-center text-sm text-gray-700">
                <span>
                  Mostrando {((pagination.page - 1) * pagination.limit) + 1} até{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                  {pagination.total} resultados
                </span>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrev}
                  className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Anterior
                </button>
                
                {[...Array(Math.min(5, pagination.totalPages))].map((_, index) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = index + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = index + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + index;
                  } else {
                    pageNum = pagination.page - 2 + index;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        pagination.page === pageNum
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext}
                  className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadDetailsModal;